import { SPEED, step } from './movement';
import { copyEntity, distance } from './prediction';
import type { Entity, WorldMap } from './protocol';

export const MAX_REMOTE_SAMPLES = 32;
export const MAX_EXTRAPOLATION_MS = 100;
interface Sample { entity: Entity; time: number }

function rotationBetween(a: Entity['rotation'], b: Entity['rotation'], t: number): Entity['rotation'] {
  const sign = a.reduce((sum, v, i) => sum + v * b[i]!, 0) < 0 ? -1 : 1;
  const q = a.map((v, i) => v * (1 - t) + sign * b[i]! * t) as Entity['rotation'];
  const length = Math.hypot(...q); return q.map(v => v / length) as Entity['rotation'];
}

export class RemoteHistory {
  private samples: Sample[] = [];
  get size(): number { return this.samples.length; }
  add(entity: Entity, time: number): void {
    const last = this.samples.at(-1);
    if (last && time <= last.time) return;
    // Versioned discontinuities never interpolate across the old trajectory.
    if (last && (time - last.time > 1000 || entity.motion !== last.entity.motion || distance(last.entity.position, entity.position) > SPEED * (time - last.time) / 1000 + .5)) this.samples = [];
    this.samples.push({ entity: copyEntity(entity), time });
    if (this.samples.length > MAX_REMOTE_SAMPLES) this.samples.shift();
    while (this.samples.length > 2 && time - this.samples[0]!.time > 2000) this.samples.shift();
  }
  sample(time: number, map: WorldMap): Entity | null {
    const first = this.samples[0], last = this.samples.at(-1);
    if (!first || !last) return null;
    if (time <= first.time) return copyEntity(first.entity);
    for (let i = 1; i < this.samples.length; i++) {
      const b = this.samples[i]!, a = this.samples[i - 1]!;
      if (time <= b.time) {
        const t = (time - a.time) / (b.time - a.time); const result = copyEntity(b.entity);
        const dx = (b.entity.position.x - a.entity.position.x) * t, dz = (b.entity.position.z - a.entity.position.z) * t;
        const length = Math.hypot(dx, dz);
        result.position = length > 0 ? step(a.entity.position, { x: dx / length, z: dz / length }, Math.min(length / SPEED, 1), map) : { ...a.entity.position };
        result.rotation = rotationBetween(a.entity.rotation, b.entity.rotation, t); return result;
      }
    }
    const result = copyEntity(last.entity), previous = this.samples.at(-2);
    const age = time - last.time;
    if (previous && last.entity.moving) {
      const seconds = (last.time - previous.time) / 1000;
      const vx = (last.entity.position.x - previous.entity.position.x) / seconds, vz = (last.entity.position.z - previous.entity.position.z) / seconds;
      const speed = Math.hypot(vx, vz);
      if (speed > 0) result.position = step(last.entity.position, { x: vx / Math.max(SPEED, speed), z: vz / Math.max(SPEED, speed) }, Math.min(age, MAX_EXTRAPOLATION_MS) / 1000, map);
    }
    if (age > MAX_EXTRAPOLATION_MS) result.moving = false;
    return result;
  }
}

export class RemotePlayback {
  private histories = new Map<string, RemoteHistory>();
  private lastTarget = -Infinity;
  private sampledAt: number | null = null;
  private latestTime = 0;
  private arrival = 0;
  private observed = false;
  bufferMS = 100;
  intervalMS = 50;
  observe(entities: ReadonlyMap<string, Entity>, self: string, serverMS: number, now: number): void {
    this.observed = true;
    this.latestTime = serverMS; this.arrival = now;
    for (const id of this.histories.keys()) if (!entities.has(id) || id === self) this.histories.delete(id);
    for (const [id, entity] of entities) {
      if (id === self) continue;
      let history = this.histories.get(id);
      if (!history) { history = new RemoteHistory(); this.histories.set(id, history); }
      history.add(entity, serverMS);
    }
  }
  remove(id: string): void { this.histories.delete(id); }
  sample(now: number, map: WorldMap, clock: { offset: number; uncertainty: number } | null): Map<string, Entity> {
    // Recovery may render before the next authoritative sample arrives. Do not
    // seed a playback clock at epoch zero and then spend seconds slewing to now.
    if (!this.observed) return new Map();
    this.bufferMS = Math.max(100, Math.min(400, 2 * this.intervalMS + (clock?.uncertainty ?? 0)));
    const estimatedNow = clock ? now + clock.offset : this.latestTime + Math.max(0, now - this.arrival);
    // Clock refinements/delay changes cannot rewind playback or extrapolate forever.
    const desired = Math.min(estimatedNow - this.bufferMS, this.latestTime + MAX_EXTRAPOLATION_MS + 1);
    let target = Math.max(this.lastTarget, desired);
    // Slew forward clock/buffer refinements instead of fast-forwarding an avatar
    // hundreds of milliseconds in one frame. Discontinuities still snap in history.
    if (this.sampledAt !== null && now - this.sampledAt <= 1000) target = Math.min(target, this.lastTarget + Math.max(0, now - this.sampledAt) * 1.25);
    this.sampledAt = now;
    this.lastTarget = target;
    const result = new Map<string, Entity>();
    for (const [id, history] of this.histories) { const entity = history.sample(target, map); if (entity) result.set(id, entity); }
    return result;
  }
  get sampleCount(): number { let count = 0; for (const history of this.histories.values()) count += history.size; return count; }
  get playbackLagMS(): number { return Number.isFinite(this.lastTarget) ? this.latestTime - this.lastTarget : 0; }
  reset(): void { this.histories.clear(); this.lastTarget = -Infinity; this.sampledAt = null; this.latestTime = 0; this.arrival = 0; this.observed = false; this.bufferMS = 100; }
}
